import { omit } from "lodash";
import { API_ROOT } from "../../constants";
import { Authorization, TokenType, AppContextRole } from "../../types";
import { isDevelopment } from "../../utils";

import Queue from "./Queue";

enum Headers {
  Authorization = "Authorization",
  AppContext = "App-Context",
  ContentType = "Content-Type",
  AccessControlAllowOrigin = "Access-Control-Allow-Origin",
}

const HEADER_DELIMITER = " ";

interface Common {
  headers: { [key in Headers]?: string };
}

const common: Common = {
  headers: {
    [Headers.ContentType]: "application/json", 
    [Headers.AccessControlAllowOrigin]: "https://www.mymnuvr.com",
    //[Headers.AccessControlAllowOrigin]: "http://127.0.0.1:3001",
  },
};

type UnauthorizedHandler = () => Promise<void>;

let unauthorizedHandler: UnauthorizedHandler | undefined;

const setHeader = (header: Headers, prefix: string, value?: string) => {
  common.headers = {
    ...common.headers,
    [header]: [prefix, value].join(HEADER_DELIMITER),
  };
};

const deleteHeader = (header: Headers) => {
  if (common.headers) {
    common.headers = omit(common.headers, header);
  }
};

const isHeaderSet = (header: Headers) => !!common.headers[header];

export const setAuthorizationHeader = ({
  tokenType,
  accessToken,
  refreshToken,
}: Authorization) => {
  let token: string | undefined;
  if (tokenType === TokenType.Bearer) {
    token = accessToken;
  } else if (tokenType === TokenType.Refresh) {
    token = refreshToken;
  }

  setHeader(Headers.Authorization, tokenType, token);
};

export const clearAuthorizationHeader = () =>
  deleteHeader(Headers.Authorization);

export const isAuthorizationHeaderSet = () =>
  isHeaderSet(Headers.Authorization);

export const setAppContextHeader = ({ role }: { role: AppContextRole }) =>
  setHeader(Headers.AppContext, "Role", role);

export const clearContextHeader = () => deleteHeader(Headers.AppContext);

export const isContextHeaderSet = () => isHeaderSet(Headers.AppContext);

const checkIsOkay = async (response: Response) => {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text);
  }

  return response;
};

const toJSON = (response: Response) => response.json();

export function onUnauthorized(handler: UnauthorizedHandler) {
  unauthorizedHandler = handler;
}

const maybeRefreshAuthorization = async (err: unknown) => {
  const getMessage = () => {
    if (err && "message" in (err as Error)) {
      return (err as Error).message;
    }
  };

  const message = getMessage();
  const isUnauthorized = message === "unauthorized";

  if (isUnauthorized) {
    await unauthorizedHandler?.();
  } else {
    throw err;
  }
};

type CustomRequstInit = Pick<RequestInit, "method"> &
  Omit<Partial<RequestInit>, "headers">;

const fetchRequest = async (slug: string, init: CustomRequstInit) => {
  const doRequest = async () => {
    const response = await fetch(`${API_ROOT}${slug}`, {
      ...common,
      ...init,
    });

    await checkIsOkay(response);

    return toJSON(response);
  };

  const retryRequest = doRequest;

  let res: any;

  try {
    res = await doRequest();
  } catch (err) {
    await maybeRefreshAuthorization(err);
    res = await retryRequest();
  } finally {
    return res;
  }
};

/*
 * fetching one at a time helps the local lambda docker containers run more reliably
 */
export interface FetchOptions {
  force?: boolean;
}

const maybeFetchOneAtATime = (
  slug: string,
  init: CustomRequstInit,
  { force = false }: FetchOptions = {}
) => {
  const isFetchOneAtATime = isDevelopment();
  const doFetch = () => fetchRequest(slug, init);

  return isFetchOneAtATime && !force ? Queue.enqueue(doFetch) : doFetch();
};

const getRequest = (slug: string, payload?: any, options?: FetchOptions) =>
  maybeFetchOneAtATime(
    slug + (payload ? `?${new URLSearchParams(payload)}` : ""),
    {
      method: "GET",
    },
    options
  );

const postRequest = (slug: string, payload?: any) =>
  maybeFetchOneAtATime(slug, {
    method: "POST",
    body: JSON.stringify(payload),
  });

const deleteRequest = (slug: string) =>
  maybeFetchOneAtATime(slug, {
    method: "DELETE",
  });

export { getRequest as get, postRequest as post, deleteRequest as delete };
